import React, { useState, useEffect, useContext, useRef } from "react";
import { FaUserCircle, FaBell, FaUsers, FaHome, FaCalendarAlt, FaNewspaper, FaBook, FaUsers as FaCommittee, FaBlog, FaImages, FaInfoCircle, FaSignOutAlt, FaCog, FaUser } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { ThreeDots } from "react-loader-spinner";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSearch } from "../../Context/SearchContext";
import { NotificationContext } from "../../Context/NotificationContext";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import logo from "./logo.png"

const Header = () => {
  const [activePopup, setActivePopup] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const token = Cookies.get("userToken");
  const popupRef = useRef(null);

  const [isLoggedIn, setIsLoggedIn] = useState(!!Cookies.get("userToken"));
  const { setSelectedEvent } = useSearch();
  const notificationContext = useContext(NotificationContext);
  const { unreadCount = 0, fetchReceivedRequests } = notificationContext || {};

  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setActivePopup(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close popup on escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setActivePopup(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/apis/user/requestPending`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        setInvitations(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch invitations", err);
      }
    };

    if (token) {
      fetchInvitations();
      const interval = setInterval(fetchInvitations, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const handleLoginLogout = async () => {
    if (isLoggedIn) {
      setIsLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        Cookies.remove("userToken");
        setIsLoggedIn(false);
        toast.success("Successfully logged out!", {
          position: "top-right",
          autoClose: 3000,
        });
        navigate("/login");
      } catch (error) {
        toast.error("Logout failed. Please try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      navigate("/login");
    }
  };

  const handleSeeAll = () => {
    navigate("/connections");
    setActivePopup(null);
  };

  const handleAccept = async (id) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/apis/user/acceptRequest/${id}`,
        {},
        {
          headers: { Authorization: `Bearer ${Cookies.get("userToken")}` },
        }
      );
      setInvitations((prev) => prev.filter((inv) => inv._id !== id));
      toast.success(res.data.message);
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/apis/user/rejectRequest/${id}`,
        {},
        {
          headers: { Authorization: `Bearer ${Cookies.get("userToken")}` },
        }
      );
      setInvitations((prev) => prev.filter((inv) => inv._id !== id));
      toast.success(res.data.message);
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  // Navigation items
  const navItems = [
    { path: "home", label: "Home", icon: FaHome },
    { path: "event", label: "Event", icon: FaCalendarAlt },
    { path: "notice", label: "Notice", icon: FaNewspaper },
    { path: "courses", label: "Courses", icon: FaBook },
    { path: "committee", label: "Committee", icon: FaCommittee },
    { path: "blog", label: "Blog", icon: FaBlog },
    { path: "gallery", label: "Gallery", icon: FaImages },
    { path: "about", label: "About", icon: FaInfoCircle },
  ];

  const isActive = (path) => location.pathname === `/${path}`;

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] backdrop-blur-md">
          <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center">
            <ThreeDots height="60" width="60" color="#64748b" ariaLabel="loading" />
            <p className="text-gray-700 font-semibold mt-4">Logging out...</p>
          </div>
        </div>
      )}

      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <header className="w-full bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 shadow-lg">
          <div className="flex items-center justify-between px-2 sm:px-4 py-1.5 sm:py-2">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/home" className="relative group inline-block">
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-lg opacity-0 group-hover:opacity-30 blur transition duration-300"></div>
                <div className="relative flex items-center space-x-1 sm:space-x-2 bg-white/5 backdrop-blur-sm px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-white/10 group-hover:border-white/20 transition-all duration-300">
                  <div className="relative">
                    <div className="absolute inset-0 bg-white/20 rounded-full blur-sm group-hover:bg-white/30 transition"></div>
                    <img 
                      src={logo} 
                      alt="Alumni Connect Logo" 
                      className="relative w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 object-contain filter drop-shadow-lg group-hover:scale-110 transition-transform duration-300" 
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-white font-bold text-[10px] sm:text-xs md:text-sm tracking-wide leading-tight">Alumni</span>
                    <span className="text-amber-300 text-[8px] sm:text-[10px] md:text-xs font-medium -mt-0.5 sm:-mt-1 leading-tight">Connect</span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 flex justify-center items-center space-x-0.5 sm:space-x-1 px-1 sm:px-2 overflow-x-auto scrollbar-hide">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={`/${item.path}`}
                    className={`group relative px-1.5 sm:px-2 md:px-3 py-1 sm:py-1.5 rounded-lg transition-all duration-300 ${
                      active ? 'bg-slate-700/80' : 'hover:bg-slate-700/50'
                    }`}
                  >
                    <div className="flex items-center space-x-0.5 sm:space-x-1 md:space-x-2">
                      <Icon className={`text-[10px] sm:text-xs md:text-sm transition-colors ${
                        active ? 'text-amber-300' : 'text-slate-400 group-hover:text-amber-300'
                      }`} />
                      <span className={`text-[10px] sm:text-xs md:text-sm font-medium capitalize whitespace-nowrap transition-colors ${
                        active ? 'text-white' : 'text-slate-300 group-hover:text-white'
                      }`}>
                        {item.label}
                      </span>
                    </div>
                    {active && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-400" />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Icons */}
            <div className="flex-shrink-0 flex items-center space-x-2 sm:space-x-3 md:space-x-4">
              {/* Connections */}
              <div className="relative cursor-pointer group" onClick={() => setActivePopup(activePopup === "connections" ? null : "connections")}>
                <FaUsers className="text-slate-300 text-sm sm:text-base md:text-lg group-hover:text-amber-300 transition" />
                {invitations.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[8px] sm:text-[10px] font-bold rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 flex items-center justify-center shadow-lg">
                    {invitations.length}
                  </span>
                )}
              </div>

              {/* Notifications */}
              <div className="relative cursor-pointer group" onClick={() => navigate("/notifications")}>
                <FaBell className="text-slate-300 text-sm sm:text-base md:text-lg group-hover:text-amber-300 transition" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[8px] sm:text-[10px] font-bold rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 flex items-center justify-center shadow-lg">
                    {unreadCount}
                  </span>
                )}
              </div>

              {/* Profile */}
              <div className="relative cursor-pointer group" onClick={() => setActivePopup(activePopup === "profile" ? null : "profile")}>
                <FaUserCircle className="text-slate-300 text-sm sm:text-base md:text-lg group-hover:text-amber-300 transition" />
              </div>
            </div>
          </div>
        </header>
      </div>

      {/* Spacer */}
      <div className="h-[52px] sm:h-[60px]"></div>

      {/* Profile Popup */}
      <AnimatePresence>
        {activePopup === "profile" && (
          <motion.div
            ref={popupRef}
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed top-12 sm:top-14 right-2 sm:right-4 w-64 sm:w-72 bg-white/95 backdrop-blur-md shadow-2xl rounded-xl p-4 z-[60] border border-slate-200"
          >
            <div className="flex items-center space-x-3 mb-4 pb-3 border-b border-slate-200">
              <div className="w-8 h-8 bg-gradient-to-r from-slate-600 to-slate-700 rounded-full flex items-center justify-center">
                <FaUser className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-bold bg-gradient-to-r from-slate-600 to-slate-700 bg-clip-text text-transparent">
                Profile Menu
              </h3>
            </div>

            <div className="flex flex-col space-y-1">
              <button
                onClick={() => { navigate("/profile"); setActivePopup(null); }}
                className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-amber-50 transition group w-full text-left"
              >
                <div className="w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 transition">
                  <FaUser className="w-3 h-3 text-amber-600" />
                </div>
                <span className="text-sm text-gray-700 font-medium">View Profile</span>
              </button>

              <button
                onClick={handleLoginLogout}
                className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-red-50 transition group w-full text-left"
              >
                <div className="w-7 h-7 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition">
                  <FaSignOutAlt className="w-3 h-3 text-red-600" />
                </div>
                <span className="text-sm text-gray-700 font-medium">{isLoggedIn ? "Logout" : "Login"}</span>
              </button>
            </div>

            <button
              onClick={() => setActivePopup(null)}
              className="text-gray-500 hover:text-gray-700 mt-3 block w-full text-center text-xs font-medium py-2 border-t border-slate-200"
            >
              Close
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Connections Popup */}
      <AnimatePresence>
        {activePopup === "connections" && (
          <motion.div
            ref={popupRef}
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed top-12 sm:top-14 right-2 sm:right-4 w-72 sm:w-80 bg-white/95 backdrop-blur-md shadow-2xl rounded-xl p-4 z-[60] max-h-96 overflow-y-auto border border-slate-200"
          >
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-200 sticky top-0 bg-white/95 backdrop-blur-md">
              <div className="flex items-center space-x-2">
                <FaUsers className="text-amber-600 text-sm" />
                <h3 className="text-base font-bold bg-gradient-to-r from-amber-600 to-amber-700 bg-clip-text text-transparent">
                  Connection Requests
                </h3>
              </div>
              <button
                onClick={handleSeeAll}
                className="text-xs font-semibold text-amber-600 hover:text-amber-700 bg-amber-50 px-2 py-1 rounded-lg transition"
              >
                See All →
              </button>
            </div>

            {invitations.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-xs text-gray-500 font-medium">No pending invitations</p>
              </div>
            ) : (
              invitations.slice(0, 5).map((inv, index) => (
                <div
                  key={inv._id}
                  className="border-l-4 border-amber-400 bg-gradient-to-r from-amber-50 to-transparent p-3 mb-2 rounded-lg hover:shadow-md transition"
                >
                  <div className="flex items-start gap-2">
                    <img
                      src={inv.image || "/assets/DefaultImage.jpg"}
                      alt="inviter"
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-amber-300"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/40";
                      }}
                    />
                    <div className="flex-1">
                      <p className="text-xs font-bold text-gray-800">{inv.fullName || "Unknown"}</p>
                      <p className="text-[10px] text-gray-600 truncate">{inv.email || "No email"}</p>
                      <div className="flex gap-2 mt-2">
                        <button
                          className="text-[10px] px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition"
                          onClick={() => handleReject(inv._id)}
                        >
                          Decline
                        </button>
                        <button
                          className="text-[10px] px-2 py-1 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg font-semibold hover:shadow-lg transition"
                          onClick={() => handleAccept(inv._id)}
                        >
                          Accept
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}

            <button
              className="mt-3 w-full text-xs text-gray-500 hover:text-gray-700 font-medium py-2 border-t border-slate-200"
              onClick={() => setActivePopup(null)}
            >
              Close
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default Header;